import { useState } from "react";
import { apiClient, REPORT_TYPES, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { PackagePlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * HouseholdOrderDialog — adds a set of services to every eligible member
 * screening in a household in one action (P1 backlog, Feb 2026).
 *
 * Backend endpoint: POST /api/groups/{gid}/order/add-services
 * Response is per-member (applied / skipped / already_ordered) so we
 * summarise it in a follow-up toast instead of throwing on partial success.
 */
export default function HouseholdOrderDialog({ group, onOrdered }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const memberCount = ((group?.member_screenings || group?.members || []).filter((m) => !m.removed).length);

  const toggle = (key) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const submit = async () => {
    const services = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (services.length === 0) {
      toast.error("Select at least one service.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post(
        `/groups/${group.id}/order/add-services`,
        { services, reason: reason.trim() || null },
      );
      setResult(data);
      if (data.applied_to_count > 0) {
        toast.success(
          `Added ${services.length} service${services.length > 1 ? "s" : ""} to ${data.applied_to_count} household member${data.applied_to_count > 1 ? "s" : ""}.`,
        );
      } else {
        toast.warning("No members were eligible (all closed / archived / already ordered).");
      }
      onOrdered?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Household order failed.");
    } finally { setBusy(false); }
  };

  const close = () => {
    setOpen(false);
    // Reset a beat later so the fade-out isn't jarring.
    setTimeout(() => {
      setSelected({});
      setReason("");
      setResult(null);
    }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="rounded-md border-slate-300"
          data-testid="household-order-btn"
          disabled={memberCount === 0 || group?.archived_at}
        >
          <PackagePlus className="h-4 w-4 mr-2" />
          Order for household
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Order services for household</DialogTitle>
          <DialogDescription>
            Adds the selected services to every applicant in <span className="font-semibold">{group?.name}</span>{" "}
            ({memberCount} member{memberCount === 1 ? "" : "s"}). Closed or archived applicants are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="household-order-services">
              {REPORT_TYPES.map((rt) => (
                <label
                  key={rt.key}
                  htmlFor={`hh-svc-${rt.key}`}
                  className="flex items-start gap-3 rounded-md border border-slate-200 p-3 hover:border-slate-300 cursor-pointer"
                  data-testid={`household-order-svc-${rt.key}`}
                >
                  <Checkbox
                    id={`hh-svc-${rt.key}`}
                    checked={!!selected[rt.key]}
                    onCheckedChange={() => toggle(rt.key)}
                    className="mt-0.5"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{rt.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{rt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <div>
              <Label htmlFor="hh-order-reason" className="text-sm">Reason (optional)</Label>
              <Textarea
                id="hh-order-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., PM requested credit + criminal reports across all household members."
                className="mt-1"
                data-testid="household-order-reason"
              />
            </div>
          </>
        )}

        {result && (
          <div className="space-y-3 max-h-72 overflow-y-auto" data-testid="household-order-result">
            <div className="text-sm text-slate-700">
              <span className="font-semibold text-slate-900">{result.applied_to_count}</span> applicant
              {result.applied_to_count === 1 ? "" : "s"} updated ·{" "}
              <span className="font-semibold text-slate-900">{result.skipped_count}</span> skipped
            </div>
            <ul className="space-y-1.5 text-xs">
              {result.per_member?.map((m) => (
                <li
                  key={m.screening_id}
                  className="flex items-start gap-2 border-t border-slate-100 pt-1.5 first:border-0 first:pt-0"
                >
                  <CheckCircle2
                    className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${
                      m.skipped ? "text-slate-300" : "text-emerald-600"
                    }`}
                  />
                  <div className="min-w-0">
                    <div className="text-slate-800 font-medium truncate">{m.applicant_name}</div>
                    {m.skipped ? (
                      <div className="text-slate-500">{m.skip_reason}</div>
                    ) : (
                      <div className="text-slate-500">
                        Added: {m.applied.join(", ")}
                        {m.already_ordered?.length ? ` · Already had: ${m.already_ordered.join(", ")}` : ""}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={close} data-testid="household-order-cancel">Cancel</Button>
              <Button
                onClick={submit}
                disabled={busy || memberCount === 0}
                className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white"
                data-testid="household-order-confirm"
              >
                {busy ? "Placing order..." : "Place household order"}
              </Button>
            </>
          ) : (
            <Button onClick={close} className="bg-[#0A192F] text-white" data-testid="household-order-done">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
